import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spin, message, Alert, Modal } from 'antd';
import { reportAPI } from '../services/api';
import { getGuestReport } from '../utils/guestUser';
import { bodyTypeData } from '../data/bodyTypeData';
import { bodyTypeSymptoms } from '../data/bodyTypeSymptoms';
import './ResultPage.css';

// 汤剂图片映射（使用public目录下的图片）
const soupImages = {
  '平和质': '/images/soups/平和质.png',
  '气虚质': '/images/soups/气虚质.png',
  '阳虚质': '/images/soups/阳虚质.png',
  '阴虚质': '/images/soups/阴虚质.png',
  '痰湿质': '/images/soups/痰湿质.png',
  '湿热质': '/images/soups/湿热质.png',
  '血瘀质': '/images/soups/血瘀质.png',
  '气郁质': '/images/soups/气郁质.png',
  '特禀质': '/images/soups/特禀质.png'
};

const ResultPage = ({ currentUser }) => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isGuestReport, setIsGuestReport] = useState(false);

  useEffect(() => {
    fetchReport();
  }, [reportId]);

  const fetchReport = async () => {
    try {
      // 先尝试从localStorage读取游客报告
      const guestReport = getGuestReport(reportId);
      if (guestReport) {
        setReport(guestReport);
        setIsGuestReport(true);
        setLoading(false);
        return;
      }

      // 如果不是游客报告，从服务器获取
      const response = await reportAPI.getReport(reportId);
      setReport(response.data.report);
      setIsGuestReport(false);
    } catch (error) {
      message.error('获取报告失败');
      console.error('获取报告错误:', error);
    } finally {
      setLoading(false);
    }
  };

  // 游客升级 - 引导返回首页并自动打开注册弹窗
  const handleUpgradeToUser = () => {
    Modal.confirm({
      title: '注册账号',
      content: '注册后可永久保存检测数据，并在任何设备上查看。是否返回首页注册？',
      okText: '返回首页注册',
      cancelText: '继续游客体验',
      onOk: () => {
        navigate('/?action=register');
      }
    });
  };

  if (loading) {
    return (
      <div className="container">
        <div className="page-wrapper">
          <Spin size="large" tip="加载中..." />
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="container">
        <div className="page-wrapper">
          <div className="error-message">报告不存在</div>
          <button className="button-primary" onClick={() => navigate('/')}>
            返回首页
          </button>
        </div>
      </div>
    );
  }

  const API_BASE_URL = window.location.hostname === 'localhost' ? 'http://localhost:8080' : window.location.hostname;

  return (
    <div className="container">
      <div className="page-wrapper result-page">
        <div className="report-header">
          <h1 className="page-title">检测报告</h1>
          <div className="report-date">
            检测时间：{new Date(report.created_at).toLocaleString('zh-CN')}
          </div>
        </div>

        {isGuestReport && (
          <Alert
            message="游客模式报告"
            description={
              <div>
                <p>此报告临时保存在您的浏览器中，清除浏览器数据后将丢失。</p>
                <p>
                  <strong>注册账号</strong>可永久保存报告，随时随地查看。
                  <a onClick={handleUpgradeToUser} style={{ marginLeft: '8px', cursor: 'pointer', color: '#b8860b' }}>
                    立即注册 →
                  </a>
                </p>
              </div>
            }
            type="warning"
            showIcon
            closable
            style={{ marginBottom: '24px' }}
          />
        )}

        <div className="result-card body-type-card">
          <div className="card-header">
            <div className="card-icon">🏥</div>
            <h2 className="card-title">您的体质类型</h2>
          </div>
          <div className="body-type-result">
            <div className="body-type-name">{report.body_type}</div>
            <div className="body-type-description">{report.body_type_desc}</div>
            {report.score && report.score > 0 && (
              <div className="body-type-score">{report.score.toFixed(1)}分</div>
            )}
          </div>
        </div>


        <div className="analysis-section">
          <div className="analysis-card">
            <div className="card-header">
              <div className="card-icon">👅</div>
              <h3 className="card-title">舌诊分析</h3>
            </div>
            <div className="image-preview">
              <img 
                src={`${report.tongue_image_path}`}
                alt="舌诊" 
                className="diagnostic-image"
              />
            </div>
            <div className="analysis-content">
              {report.tongue_analysis}
            </div>
          </div>

          <div className="analysis-card">
            <div className="card-header">
              <div className="card-icon">👤</div>
              <h3 className="card-title">面诊分析</h3>
            </div>
            <div className="image-preview">
              <img 
                src={`${report.face_image_path}`}
                alt="面诊" 
                className="diagnostic-image"
              />
            </div>
            <div className="analysis-content">
              {report.face_analysis}
            </div>
          </div>
        </div>

        {bodyTypeSymptoms[report.body_type] && bodyTypeSymptoms[report.body_type].healthRadar && (
          <>
            {/* 注意脏腑 */}
            <div className="result-card organ-card">
              <div className="card-header">
                <div className="card-icon">🫀</div>
                <h2 className="card-title">注意脏腑</h2>
              </div>
              <div className="organ-card-content">
                <div className="organ-name-large">{bodyTypeSymptoms[report.body_type].healthRadar.organ}</div>
                {bodyTypeSymptoms[report.body_type].healthRadar.organImage && (
                  <div className="organ-image-container-large">
                    <img
                      src={bodyTypeSymptoms[report.body_type].healthRadar.organImage}
                      alt={bodyTypeSymptoms[report.body_type].healthRadar.organ}
                      className="organ-image-large"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* 健康雷达 */}
            <div className="result-card health-radar-card">
              <div className="card-header">
                <div className="card-icon">📊</div>
                <h2 className="card-title">健康雷达</h2>
              </div>
              <div className="health-radar-content">
                <div className="radar-symptoms">
                  <div className="radar-label">大数据提示容易伴有：</div>
                  <div className="radar-symptoms-list">
                    {bodyTypeSymptoms[report.body_type].healthRadar.symptoms.map((symptom, index) => (
                      <span key={index} className="radar-symptom-tag">{symptom}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {bodyTypeSymptoms[report.body_type] && (
          <>

            {/* 症状展示 */}
            {bodyTypeSymptoms[report.body_type].symptoms && bodyTypeSymptoms[report.body_type].symptoms.length > 0 && (
              <div className="result-card symptoms-card">
                <div className="card-header">
                  <div className="card-icon">📖</div>
                  <h2 className="card-title">{report.body_type === '气虚质' ? '气虚无力的症状' : '阴虚内热的症状'}</h2>
                </div>
                {report.body_type === '阴虚质' && (
                  <div className="symptoms-intro">
                    水少而火旺，阴少而阳多
                  </div>
                )}
                <div className="symptoms-content">
                  {bodyTypeSymptoms[report.body_type].symptoms.map((symptom, index) => (
                    <div key={index} className="symptom-item">
                      <div className="symptom-image-container">
                        <img
                          src={symptom.image}
                          alt={symptom.title}
                          className="symptom-image"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const container = e.target.parentElement;
                            if (container) {
                              container.style.display = 'none';
                            }
                          }}
                        />
                      </div>
                      <div className="symptom-content">
                        <h3 className="symptom-title">{symptom.title}</h3>
                        <p className="symptom-description">{symptom.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 原因分析 */}
            {bodyTypeSymptoms[report.body_type].causes && bodyTypeSymptoms[report.body_type].causes.length > 0 && (
              <div className="result-card causes-card">
                <div className="card-header">
                  <div className="card-icon">🔍</div>
                  <h2 className="card-title">{report.body_type === '气虚质' ? '造成气虚无力的原因分析' : '造成阴虚内热的原因分析'}</h2>
                </div>
                {report.body_type === '阴虚质' && (
                  <div className="causes-intro">
                    阴虚者内火攻心，常感觉胸口烦闷，情绪不稳定，注意力不集中，晚上容易惊棒失眠。
                  </div>
                )}
                <div className="causes-content">
                  {bodyTypeSymptoms[report.body_type].causes.map((cause, index) => (
                    <div key={index} className="cause-item">
                      <div className="cause-image-container">
                        <img
                          src={cause.image}
                          alt={cause.title}
                          className="cause-image"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const container = e.target.parentElement;
                            if (container) {
                              container.style.display = 'none';
                            }
                          }}
                        />
                      </div>
                      <div className="cause-content">
                        <h3 className="cause-title">{cause.title}</h3>
                        <p className="cause-description">{cause.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {bodyTypeData[report.body_type] && (
          <>
            {/* 核心病机与疾病风险 */}
            <div className="result-card disease-risk-card">
              <div className="card-header">
                <div className="card-icon">⚠️</div>
                <h2 className="card-title">核心病机与疾病风险</h2>
              </div>
              <div className="disease-risk-content">
                <div className="diet-item">
                  <div className="diet-label">核心病机：</div>
                  <div className="diet-value">{bodyTypeData[report.body_type].corePathology}</div>
                </div>
                
                <div className="diet-item">
                  <div className="diet-label">详细疾病风险：</div>
                  <div className="diet-value">{bodyTypeData[report.body_type].diseaseRisks}</div>
                </div>
              </div>
            </div>

            {/* 饮食推荐 */}
            <div className="result-card diet-recommend-card">
              <div className="card-header">
                <div className="card-icon">🥗</div>
                <h2 className="card-title">饮食推荐</h2>
              </div>
              <div className="diet-recommend-content">
                <div className="diet-item">
                  <div className="diet-label">饮食调理核心原则：</div>
                  <div className="diet-value">{bodyTypeData[report.body_type].dietPrinciple}</div>
                </div>
                
                <div className="diet-item">
                  <div className="diet-label">重点推荐：</div>
                  <div className="diet-value">
                    <ul className="diet-list">
                      {bodyTypeData[report.body_type].recommendedFoods.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* 严格禁忌 */}
            <div className="result-card diet-prohibition-card">
              <div className="card-header">
                <div className="card-icon">🚫</div>
                <h2 className="card-title">严格禁忌</h2>
              </div>
              <div className="diet-prohibition-content">
                <div className="diet-item">
                  <div className="diet-label">以下食物请严格避免：</div>
                  <div className="diet-value">
                    <ul className="diet-list prohibition-list">
                      {bodyTypeData[report.body_type].strictProhibitions.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* 推荐汤品 - 移到最后 */}
        <div className="result-card recommendation-card">
          <div className="card-header">
            <div className="card-icon">🍲</div>
            <h2 className="card-title">推荐汤品</h2>
          </div>
          <div className="recommendation-content">
            <div className="soup-display">
              <div className="soup-image-container">
                <img
                  src={soupImages[report.body_type]}
                  alt={report.recommended_soup}
                  className="soup-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <div className="soup-details">
                <div className="soup-name">{report.recommended_soup}</div>
                <div className="soup-description">{report.soup_description}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="tips-section">
          <div className="tips-title">💡 温馨提示</div>
          <div className="tips-content">
            <p>• 本检测结果仅供参考，建议定期复查</p>
            <p>• 如有身体不适，请及时咨询专业医师</p>
            <p>• 养生需要持之以恒，配合良好的生活习惯</p>
            <p>• 欢迎到我们的场馆品鉴推荐的养生汤品</p>
          </div>
        </div>

        <div className="button-group">
          <button className="button-primary" onClick={() => navigate('/detection')}>
            再次检测
          </button>
          <button className="button-secondary" onClick={() => navigate('/history')}>
            查看历史报告
          </button>
          <button className="button-secondary" onClick={() => navigate('/')}>
            返回首页
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResultPage;

